import { useState, useMemo } from 'react';
import { Download, Loader2 } from 'lucide-react';
import api from '../services/api';
import PdfPreview from '../components/PdfPreview';
import ToolPageSections from '../components/ToolPageSections';
import FontSelect from '../components/FontSelect';

// Available cursive fonts (matching printablehandwriting.com)
const FONTS = [
  { id: 'prima-cursive', name: 'Prima Cursive' },
  { id: 'd-nealian', name: "D'Nealian" },
  { id: 'alex-brush', name: 'Alex Brush' },
  { id: 'allura', name: 'Allura Regular' },
  { id: 'cedarville-cursive', name: 'Cedarville Cursive' },
  { id: 'dancing-script', name: 'Dancing Script' },
  { id: 'great-vibes', name: 'Great Vibes' },
  { id: 'italianno', name: 'Italianno' },
  { id: 'satisfy', name: 'Satisfy' }
];

const PAPER_SIZES = [
  { id: 'a4', name: 'A4 (210 × 297 mm)', width: 210, height: 297 },
  { id: 'letter', name: 'US Letter (8.5 × 11 in)', width: 215.9, height: 279.4 }
];

const LINE_SPACING_OPTIONS = [
  { id: 'narrow', name: 'Narrow' },
  { id: 'normal', name: 'Normal' },
  { id: 'wide', name: 'Wide' }
];

const TEXT_STYLE_OPTIONS = [
  { id: 'solid', name: 'Solid' },
  { id: 'dotted', name: 'Dotted' },
  { id: 'outlined', name: 'Outlined' }
];

const PRACTICE_MODE_OPTIONS = [
  { id: 'tracing-every-line', name: 'Tracing (text on every line)' },
  { id: 'tracing', name: 'Tracing (text on first line, blank lines below)' }
];

export default function CursiveHandwritingWorksheet() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Content settings
  const [practiceText, setPracticeText] = useState('Start creating your own personalized handwriting worksheets');
  const [worksheetTitle, setWorksheetTitle] = useState('');

  // Font settings - default to prima-cursive like printablehandwriting.com
  const [fontFamily, setFontFamily] = useState('prima-cursive');

  // Appearance settings
  const [lineColor, setLineColor] = useState('#cccccc');
  const [textStyle, setTextStyle] = useState('solid');
  const [textOpacity, setTextOpacity] = useState(35);
  const [textThickness, setTextThickness] = useState(1);
  const [textColor, setTextColor] = useState('#D3D3D3');

  // Page layout settings
  const [paperSize, setPaperSize] = useState('a4');
  const [orientation, setOrientation] = useState('tall');
  const [lineSpacing, setLineSpacing] = useState('normal');

  // Advanced settings
  const [practiceMode, setPracticeMode] = useState('tracing');
  const [showNameField, setShowNameField] = useState(true);
  const [nameFieldLabel, setNameFieldLabel] = useState('Name');
  const [showDateField, setShowDateField] = useState(true);
  const [dateFieldLabel, setDateFieldLabel] = useState('Date');

  // Memoize settings object to avoid unnecessary re-renders
  const previewSettings = useMemo(() => ({
    text: practiceText,
    worksheetTitle,
    font: fontFamily,
    lineColor,
    textStyle,
    textOpacity,
    textThickness,
    textColor,
    paperSize,
    orientation,
    lineSpacing,
    practiceMode,
    showNameField,
    nameFieldLabel,
    showDateField,
    dateFieldLabel
  }), [practiceText, worksheetTitle, fontFamily, lineColor, textStyle, textOpacity, textThickness, textColor, paperSize, orientation, lineSpacing, practiceMode, showNameField, nameFieldLabel, showDateField, dateFieldLabel]);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post('/worksheets/generate', {
        toolType: 'cursive',
        text: practiceText,
        worksheetTitle,
        font: fontFamily,
        lineColor,
        textStyle,
        textOpacity,
        textThickness,
        textColor,
        paperSize,
        orientation,
        lineSpacing,
        practiceMode,
        showNameField,
        nameFieldLabel,
        showDateField,
        dateFieldLabel
      }, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bastion-cursive-worksheet.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      setError('Failed to generate worksheet. Please try again.');
      console.error('Generate error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <nav className="text-sm text-gray-600">
          <span className="hover:text-gray-700 cursor-pointer">Home</span>
          <span className="mx-2">&gt;</span>
          <span className="hover:text-gray-700 cursor-pointer">All Tools</span>
          <span className="mx-2">&gt;</span>
          <span className="text-bastion-black font-medium">Cursive Handwriting Worksheets</span>
        </nav>
      </div>

      {/* Hero Section */}
      <div className="text-center py-8 px-4">
        <h1 className="text-4xl md:text-5xl font-bold text-bastion-black mb-4">
          Free <span className="text-bastion-black">Cursive Handwriting</span>
          <br />Worksheet Generator
        </h1>
        <p className="text-gray-700 max-w-2xl mx-auto mb-6">
          Practice beautiful cursive writing with our free worksheets. Learn to write smooth,
          connected letters with confidence. Download your free printable PDFs instantly.
        </p>
        <div className="flex flex-wrap justify-center gap-3">
          <span className="px-4 py-2 bg-bastion-gray-200 text-bastion-black rounded-full text-sm font-medium">
            Free Fonts
          </span>
          <span className="px-4 py-2 bg-bastion-gray-200 text-bastion-black rounded-full text-sm font-medium">
            Practice Pages
          </span>
          <span className="px-4 py-2 bg-bastion-gray-200 text-bastion-black rounded-full text-sm font-medium">
            Printable Worksheets
          </span>
          <span className="px-4 py-2 bg-bastion-gray-200 text-bastion-black rounded-full text-sm font-medium">
            Instant Download
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Cursive Handwriting Worksheet</h2>
              <p className="text-gray-600 text-sm mb-6">Create beautiful cursive handwriting practice sheets</p>

              {/* Content Section */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Content</h3>
                <p className="text-gray-600 text-sm mb-4">Configure the text content and basic settings</p>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Practice Text</label>
                    <textarea
                      value={practiceText}
                      onChange={(e) => setPracticeText(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black resize-none"
                      rows={3}
                      maxLength={50000}
                      placeholder="Enter text to practice..."
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave empty to create a blank worksheet</p>
                    <p className="text-xs text-gray-500">{practiceText.length}/50000 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Worksheet Title</label>
                    <input
                      type="text"
                      value={worksheetTitle}
                      onChange={(e) => setWorksheetTitle(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                      placeholder="Enter worksheet title (optional)"
                      maxLength={50}
                    />
                    <p className="text-xs text-gray-500 mt-1">{worksheetTitle.length}/50 characters</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Font Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Font</h3>
              <p className="text-gray-600 text-sm mb-4">Choose and configure the font style</p>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Font Family</label>
                <FontSelect fonts={FONTS} value={fontFamily} onChange={setFontFamily} />
              </div>
            </div>

            {/* Appearance Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Appearance</h3>
              <p className="text-gray-600 text-sm mb-4">Customize colors, opacity, and text styling</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Line Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={lineColor}
                      onChange={(e) => setLineColor(e.target.value)}
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={lineColor}
                      onChange={(e) => setLineColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Style</label>
                  <select
                    value={textStyle}
                    onChange={(e) => setTextStyle(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  >
                    {TEXT_STYLE_OPTIONS.map(style => (
                      <option key={style.id} value={style.id}>{style.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Opacity</label>
                  <input
                    type="range"
                    min="20"
                    max="100"
                    value={textOpacity}
                    onChange={(e) => setTextOpacity(parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-bastion-black"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>20%</span>
                    <span>35%</span>
                    <span>50%</span>
                    <span>65%</span>
                    <span>80%</span>
                    <span>100%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Thickness</label>
                  <input
                    type="range"
                    min="0.25"
                    max="2"
                    step="0.25"
                    value={textThickness}
                    onChange={(e) => setTextThickness(parseFloat(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-bastion-black"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0.25</span>
                    <span>0.5</span>
                    <span>0.75</span>
                    <span>1</span>
                    <span>1.25</span>
                    <span>1.5</span>
                    <span>1.75</span>
                    <span>2</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Text Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="w-10 h-10 rounded border border-gray-300 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={textColor}
                      onChange={(e) => setTextColor(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Page Layout Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Page Layout</h3>
              <p className="text-gray-600 text-sm mb-4">Configure paper size, orientation, and spacing</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paper Size</label>
                  <select
                    value={paperSize}
                    onChange={(e) => setPaperSize(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  >
                    {PAPER_SIZES.map(size => (
                      <option key={size.id} value={size.id}>{size.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orientation</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="orientation"
                        value="tall"
                        checked={orientation === 'tall'}
                        onChange={(e) => setOrientation(e.target.value)}
                        className="w-4 h-4 text-bastion-black focus:ring-bastion-black"
                      />
                      <span className="text-sm">Tall</span>
                      <svg className="w-4 h-6" viewBox="0 0 16 24" fill="currentColor">
                        <rect x="1" y="1" width="14" height="22" rx="1" stroke="currentColor" strokeWidth="1" fill="none" />
                        <line x1="3" y1="4" x2="13" y2="4" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="3" y1="7" x2="13" y2="7" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="3" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth="0.5" />
                      </svg>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="orientation"
                        value="wide"
                        checked={orientation === 'wide'}
                        onChange={(e) => setOrientation(e.target.value)}
                        className="w-4 h-4 text-bastion-black focus:ring-bastion-black"
                      />
                      <span className="text-sm">Wide</span>
                      <svg className="w-6 h-4" viewBox="0 0 24 16" fill="currentColor">
                        <rect x="1" y="1" width="22" height="14" rx="1" stroke="currentColor" strokeWidth="1" fill="none" />
                        <line x1="3" y1="4" x2="21" y2="4" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="3" y1="7" x2="21" y2="7" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="3" y1="10" x2="21" y2="10" stroke="currentColor" strokeWidth="0.5" />
                      </svg>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Line Spacing</label>
                  <select
                    value={lineSpacing}
                    onChange={(e) => setLineSpacing(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  >
                    {LINE_SPACING_OPTIONS.map(spacing => (
                      <option key={spacing.id} value={spacing.id}>{spacing.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Advanced Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Advanced</h3>
              <p className="text-gray-600 text-sm mb-4">Additional settings and practice modes</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Practice Mode</label>
                  <select
                    value={practiceMode}
                    onChange={(e) => setPracticeMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                  >
                    {PRACTICE_MODE_OPTIONS.map(mode => (
                      <option key={mode.id} value={mode.id}>{mode.name}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Show Name Field</label>
                  <button
                    type="button"
                    onClick={() => setShowNameField(!showNameField)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showNameField ? 'bg-bastion-black' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showNameField ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {showNameField && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name Field Label</label>
                    <input
                      type="text"
                      value={nameFieldLabel}
                      onChange={(e) => setNameFieldLabel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">Show Date Field</label>
                  <button
                    type="button"
                    onClick={() => setShowDateField(!showDateField)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      showDateField ? 'bg-bastion-black' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showDateField ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {showDateField && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Date Field Label</label>
                    <input
                      type="text"
                      value={dateFieldLabel}
                      onChange={(e) => setDateFieldLabel(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bastion-black focus:border-bastion-black"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={loading}
              className="w-full py-4 bg-bastion-black hover:bg-bastion-primary-hover text-white font-semibold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  Generate PDF
                </>
              )}
            </button>

            {error && (
              <p className="text-red-500 text-sm text-center">{error}</p>
            )}
          </div>

          {/* Preview Panel */}
          <PdfPreview toolType="cursive" settings={previewSettings} />
        </div>

        {/* AI Analysis CTA */}
        <div className="mt-12 bg-bastion-gray-100 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Want personalized feedback on your handwriting?
          </h2>
          <p className="text-gray-700 mb-6">
            Try our AI-powered handwriting analysis tool. Get instant feedback on legibility,
            consistency, spacing, and more to improve your writing skills.
          </p>
          <a
            href="/analysis"
            className="inline-flex items-center gap-2 px-6 py-3 bg-bastion-black hover:bg-bastion-primary-hover text-white font-semibold rounded-xl transition-all"
          >
            Try AI Handwriting Analysis
            <span className="bg-white/30 text-xs px-2 py-0.5 rounded-full">Free first try</span>
          </a>
        </div>

        <ToolPageSections toolType="cursive" />
      </div>
    </div>
  );
}
